<!-- Tracker modal — single shell drives both Add Agent and Update Device.
     The mode input swaps the header copy, the save-button label, and which
     fields are locked. -->
<div
  *ngIf="open"
  class="fixed inset-0 flex items-center justify-center bg-main-background bg-opacity-75 z-30 p-4">
  <form
    (ngSubmit)="onSubmit()"
    class="bg-white/95 shadow-xl border border-gray-200 rounded-md w-full max-w-3xl max-h-[90vh] flex flex-col">
    <div class="flex items-center justify-between bg-blue-500 text-white px-5 py-3 rounded-t-md shrink-0">
      <p class="oswald-font text-lg font-medium">
        <ng-container *ngIf="mode === 'create'">Add Agent</ng-container>
        <ng-container *ngIf="mode === 'edit'">Update Device</ng-container>
      </p>
      <button
        type="button"
        (click)="onClose()"
        [disabled]="saving"
        class="text-white/80 hover:text-white disabled:opacity-60">
        <lucide-icon [img]="icons.X" [size]="18"></lucide-icon>
      </button>
    </div>

    <div class="flex-1 min-h-0 overflow-auto px-5 py-4 grid grid-cols-1 md:grid-cols-2 gap-4">
      <div class="md:col-span-2">
        <label class="block mb-1 text-xs font-bold text-gray-900">
          Unit ID <span class="text-red-500">*</span>
        </label>
        <input
          type="text"
          [ngModel]="form().id"
          (ngModelChange)="updateField('id', $event)"
          (blur)="markTouched('id')"
          name="agentId"
          [disabled]="idLocked() || saving"
          [class.border-red-400]="showError('id', !form().id.trim() && !idLocked())"
          [class.border-gray-300]="!showError('id', !form().id.trim() && !idLocked())"
          class="bg-gray-50 border text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full p-2 disabled:opacity-60" />
        <p *ngIf="showError('id', !form().id.trim() && !idLocked())" class="text-xs text-red-500 mt-1">
          Unit ID is required.
        </p>
        <p *ngIf="mode === 'create' && isWliTracker()" class="text-xs text-gray-500 mt-1">
          Derived from the WLI Serial as <code>WLI-&lt;serial&gt;</code>.
        </p>
      </div>

      <div>
        <label class="block mb-1 text-xs font-bold text-gray-900">
          Name <span class="text-red-500">*</span>
        </label>
        <input
          type="text"
          [ngModel]="form().name"
          (ngModelChange)="updateField('name', $event)"
          (blur)="markTouched('name')"
          name="agentName"
          [disabled]="saving"
          [class.border-red-400]="showError('name', !form().name.trim())"
          [class.border-gray-300]="!showError('name', !form().name.trim())"
          class="bg-gray-50 border text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full p-2 disabled:opacity-60" />
        <p *ngIf="showError('name', !form().name.trim())" class="text-xs text-red-500 mt-1">
          Name is required.
        </p>
      </div>

      <div>
        <label class="block mb-1 text-xs font-bold text-gray-900">Type</label>
        <select
          [ngModel]="form().type"
          (ngModelChange)="updateField('type', $event)"
          name="agentType"
          [disabled]="hardwareLocked() || saving"
          class="bg-gray-50 border border-gray-300 text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full p-2 disabled:opacity-60">
          <option *ngFor="let t of agentTypes" [ngValue]="t">{{ t }}</option>
          <!-- Render the existing value even if it's outside the picker
               list (e.g. legacy 'Mobile') so the dropdown isn't blank. -->
          <option
            *ngIf="form().type && !agentTypes.includes(form().type)"
            [ngValue]="form().type">
            {{ form().type }}
          </option>
        </select>
      </div>

      <div class="md:col-span-2">
        <label class="block mb-1 text-xs font-bold text-gray-900">Fleet Version</label>
        <select
          [ngModel]="form().fleetVersion"
          (ngModelChange)="updateField('fleetVersion', $event)"
          name="fleetVersion"
          [disabled]="saving"
          class="bg-gray-50 border border-gray-300 text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full p-2 disabled:opacity-60">
          <option [ngValue]="null">— Other / unset —</option>
          <option [ngValue]="1">1 — Legacy WLI tracker</option>
          <option [ngValue]="2">2 — Fleet API (BigQuery)</option>
        </select>
      </div>

      <div class="md:col-span-2">
        <label class="inline-flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            [ngModel]="form().isActive"
            (ngModelChange)="updateField('isActive', $event)"
            name="isActive"
            [disabled]="saving"
            class="sr-only peer" />
          <div
            class="relative w-11 h-6 bg-gray-300 rounded-full peer peer-focus:ring-2 peer-focus:ring-blue-300 peer-checked:bg-blue-500 after:content-[''] after:absolute after:top-0.5 after:start-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5">
          </div>
          <span class="text-xs font-bold text-gray-900">
            Active
            <span class="font-normal text-gray-500">
              (uncheck to hide this tracker from map & mobile app)
            </span>
          </span>
        </label>
      </div>

      <div class="md:col-span-2">
        <label class="block mb-1 text-xs font-bold text-gray-900">Description</label>
        <textarea
          [ngModel]="form().description"
          (ngModelChange)="updateField('description', $event)"
          name="agentDescription"
          rows="2"
          [disabled]="saving"
          class="bg-gray-50 border border-gray-300 text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full p-2 disabled:opacity-60"></textarea>
      </div>

      <div>
        <label class="block mb-1 text-xs font-bold text-gray-900">Hardware Type</label>
        <input
          type="text"
          [ngModel]="form().hardwareType"
          (ngModelChange)="updateField('hardwareType', $event)"
          name="hardwareType"
          [disabled]="hardwareLocked() || saving"
          class="bg-gray-50 border border-gray-300 text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full p-2 disabled:opacity-60" />
      </div>
      <div>
        <label class="block mb-1 text-xs font-bold text-gray-900">Hardware Name</label>
        <input
          type="text"
          [ngModel]="form().hardwareName"
          (ngModelChange)="updateField('hardwareName', $event)"
          name="hardwareName"
          [disabled]="hardwareLocked() || saving"
          class="bg-gray-50 border border-gray-300 text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full p-2 disabled:opacity-60" />
      </div>
      <div>
        <label class="block mb-1 text-xs font-bold text-gray-900">Model Number</label>
        <input
          type="text"
          [ngModel]="form().hardwareModelNumber"
          (ngModelChange)="updateField('hardwareModelNumber', $event)"
          name="hardwareModelNumber"
          [disabled]="hardwareLocked() || saving"
          class="bg-gray-50 border border-gray-300 text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full p-2 disabled:opacity-60" />
      </div>
      <div>
        <label class="block mb-1 text-xs font-bold text-gray-900">
          <ng-container *ngIf="mode === 'create' && isWliTracker(); else genericSerialLabel">
            WLI Serial
          </ng-container>
          <ng-template #genericSerialLabel>Serial Number</ng-template>
          <span class="text-red-500">*</span>
        </label>
        <input
          type="text"
          [ngModel]="form().hardwareSerialNumber"
          (ngModelChange)="updateField('hardwareSerialNumber', $event)"
          (blur)="markTouched('hardwareSerialNumber')"
          name="hardwareSerialNumber"
          [disabled]="hardwareLocked() || saving"
          [placeholder]="mode === 'create' && isWliTracker() ? '862921' : ''"
          [class.border-red-400]="showError('hardwareSerialNumber', (!form().hardwareSerialNumber.trim() && !hardwareLocked()) || serialFormatInvalid())"
          [class.border-gray-300]="!showError('hardwareSerialNumber', (!form().hardwareSerialNumber.trim() && !hardwareLocked()) || serialFormatInvalid())"
          class="bg-gray-50 border text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full p-2 disabled:opacity-60" />
        <p *ngIf="showError('hardwareSerialNumber', !form().hardwareSerialNumber.trim() && !hardwareLocked())" class="text-xs text-red-500 mt-1">
          Serial Number is required.
        </p>
        <p *ngIf="showError('hardwareSerialNumber', serialFormatInvalid())" class="text-xs text-red-500 mt-1">
          WLI serial must be 6-7 digits (e.g. <code>862921</code>).
        </p>
      </div>

      <div>
        <label class="block mb-1 text-xs font-bold text-gray-900">Phone Number</label>
        <input
          type="text"
          [ngModel]="form().phoneNumber"
          (ngModelChange)="updateField('phoneNumber', $event)"
          name="phoneNumber"
          [disabled]="hardwareLocked() || saving"
          class="bg-gray-50 border border-gray-300 text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full p-2 disabled:opacity-60" />
      </div>
      <div>
        <label class="block mb-1 text-xs font-bold text-gray-900">IMEI</label>
        <input
          type="text"
          [ngModel]="form().imei"
          (ngModelChange)="updateField('imei', $event)"
          name="imei"
          [disabled]="hardwareLocked() || saving"
          class="bg-gray-50 border border-gray-300 text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full p-2 disabled:opacity-60" />
      </div>
      <div>
        <label class="block mb-1 text-xs font-bold text-gray-900">ICCID</label>
        <input
          type="text"
          [ngModel]="form().iccid"
          (ngModelChange)="updateField('iccid', $event)"
          name="iccid"
          [disabled]="hardwareLocked() || saving"
          class="bg-gray-50 border border-gray-300 text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full p-2 disabled:opacity-60" />
      </div>

      <div>
        <label class="block mb-1 text-xs font-bold text-gray-900">OS Name</label>
        <input
          type="text"
          [ngModel]="form().osName"
          (ngModelChange)="updateField('osName', $event)"
          name="osName"
          [disabled]="hardwareLocked() || saving"
          class="bg-gray-50 border border-gray-300 text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full p-2 disabled:opacity-60" />
      </div>
      <div>
        <label class="block mb-1 text-xs font-bold text-gray-900">OS Version</label>
        <input
          type="text"
          [ngModel]="form().osVersion"
          (ngModelChange)="updateField('osVersion', $event)"
          name="osVersion"
          [disabled]="hardwareLocked() || saving"
          class="bg-gray-50 border border-gray-300 text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full p-2 disabled:opacity-60" />
      </div>

      <div>
        <label class="block mb-1 text-xs font-bold text-gray-900">App Name</label>
        <input
          type="text"
          [ngModel]="form().appName"
          (ngModelChange)="updateField('appName', $event)"
          name="appName"
          [disabled]="hardwareLocked() || saving"
          class="bg-gray-50 border border-gray-300 text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full p-2 disabled:opacity-60" />
      </div>
      <div>
        <label class="block mb-1 text-xs font-bold text-gray-900">App Version</label>
        <input
          type="text"
          [ngModel]="form().appVersion"
          (ngModelChange)="updateField('appVersion', $event)"
          name="appVersion"
          [disabled]="hardwareLocked() || saving"
          class="bg-gray-50 border border-gray-300 text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full p-2 disabled:opacity-60" />
      </div>

      <div>
        <label class="block mb-1 text-xs font-bold text-gray-900">Timeout Online (sec)</label>
        <input
          type="number"
          [ngModel]="form().timeoutOnline"
          (ngModelChange)="updateField('timeoutOnline', $event)"
          name="timeoutOnline"
          min="0"
          [disabled]="saving"
          class="bg-gray-50 border border-gray-300 text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full p-2 disabled:opacity-60" />
      </div>
      <div>
        <label class="block mb-1 text-xs font-bold text-gray-900">Timeout Active (sec)</label>
        <input
          type="number"
          [ngModel]="form().timeoutActive"
          (ngModelChange)="updateField('timeoutActive', $event)"
          name="timeoutActive"
          min="0"
          [disabled]="saving"
          class="bg-gray-50 border border-gray-300 text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full p-2 disabled:opacity-60" />
      </div>
    </div>

    <div class="flex justify-end gap-3 px-5 py-3 border-t border-gray-200 bg-white rounded-b-md shrink-0">
      <button
        type="button"
        (click)="onClose()"
        [disabled]="saving"
        class="px-5 py-2 rounded-md border border-gray-400 oswald-font disabled:opacity-60">
        Cancel
      </button>
      <button
        type="submit"
        [disabled]="saving || !canSubmit()"
        class="px-6 py-2 rounded-md bg-blue-500 hover:bg-blue-600 text-white oswald-font disabled:opacity-60 disabled:cursor-not-allowed">
        <ng-container *ngIf="!saving">
          <ng-container *ngIf="mode === 'create'">Add Agent</ng-container>
          <ng-container *ngIf="mode === 'edit'">Update Device</ng-container>
        </ng-container>
        <ng-container *ngIf="saving">Saving…</ng-container>
      </button>
    </div>
  </form>
</div>
